import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useLocation, useOutletContext, useParams } from "react-router-dom";
import { axiosPrivate } from "../../../api/axios";
import * as XLSX from 'xlsx-js-style';
import { Box, Button, Card, CardHeader, CardContent, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import CommonAgGrid from '../../common/DataGrid/CommonAgGrid';
import useGridTransaction from '../../../hooks/useGridTransaction';
import DatagridButtonGroup from '../ButtonGroup/DatagridButtonGroup';
import StatusMasterDialog from './StatusMasterDialog';

const StatusMaster = () => {
  const location = useLocation();
  const gridRef = useRef();
  const theme = useTheme();
  const [projectId, setProjectId] = useState(null);
  const { group_code, project_code } = useParams();
  const { groupName, projectName } = useOutletContext();
  const [columnDefs, setColumnDefs] = useState([]);
  const [pageSize, setPageSize] = useState(100);

  // 데이터 다이얼로그 상태
  const [dataDialogOpen, setDataDialogOpen] = useState(false);
  const [dataDialogMode, setDataDialogMode] = useState('add');
  const [currentData, setCurrentData] = useState(null);

  // URL 파라미터 처리
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const pid = queryParams.get("pid");
    if (pid) {
      setProjectId(pid);
    }
  }, [location.search]);
  
  // 데이터 조회 함수
  const fetchStatusMaster = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await axiosPrivate.post('/table/statusmaster/list', {
        project_id: projectId,
        group_code,
        project_code,
      });

      // 열 설정
      const columnConfig = {
        statusId: { width: 140, headerName: "상태 ID", editable: false },
        statusName: { width: 140, headerName: "상태명", editable: false },
        mchColor: { width: 140, headerName: "장비 색상", editable: false },
        mchBlink: { width: 140, headerName: "장비 점멸", editable: false },
        titleColor: { width: 140, headerName: "말풍선 제목 색상", editable: false },
        textColor: { width: 140, headerName: "말풍선 내용 색상", editable: false },
        textBkColor: { width: 140, headerName: "내용 배경색", editable: false },
        soundId: { width: 140, headerName: "소리 ID(미구현)", editable: false },
        updateDate: { width: 200, headerName: "업데이트", editable: false }
      };
  
      // API 응답에 columns가 없으면 columnConfig 기반으로 생성
      let fetchedColumns = [];
      
      if (res.data.columns) {
        fetchedColumns = res.data.columns.map((col) => {
          const baseConfig = {
            field: col.COLUMN_NAME,
            headerName: col.COLUMN_NAME,
            editable: false,
            type:
              col.DATA_TYPE === 'int' || col.COLUMN_NAME === 'mchId'
                ? 'numericColumn'
                : undefined,
            width: 100,
            headerClass: 'ag-header-center',
            cellStyle: { textAlign: 'center' },
          };
          
          const customConfig = columnConfig[col.COLUMN_NAME] ?? {};
          return { ...baseConfig, ...customConfig };
        });
      } else {
        // columns 정보가 없을 경우 (수동 정의)
        fetchedColumns = Object.keys(columnConfig).map((key) => {
          return {
            field: key,
            headerName: columnConfig[key].headerName,
            editable: false,
            width: columnConfig[key].width || 100,
            headerClass: 'ag-header-center',
            cellStyle: { textAlign: 'center' },
            type: ['statusId', 'mchColor', 'mchBlink', 'titleColor', 'textColor', 'textBkColor', 'soundId'].includes(key) ? 'numericColumn' : undefined,
            ...columnConfig[key]
          };
        });
      }
  
      const dataWithId = res.data.data.map((row, index) => ({
        ...row,
        id: row.statusId || index + 1, // statusId를 고유키로 사용
      }));
  
      setColumnDefs(fetchedColumns);
      setRowData(dataWithId);
      setRowTransaction({ add: [], update: [], remove: [] });
    } catch (err) {
      console.error("데이터 조회 실패:", err);
    }
  }, [projectId, group_code, project_code]);
  
  // 트랜잭션 훅 사용
  const {
    rowData,
    setRowData,
    selectedRows,
    setSelectedRows,
    rowTransaction,
    setRowTransaction,
    handleAddRow: baseHandleAddRow,
    handleDeleteRow: baseHandleDeleteRow,
    handleSaveRow: baseHandleSaveRow,
    onCellValueChanged: baseOnCellValueChanged
  } = useGridTransaction(fetchStatusMaster);

  // 데이터 로드
  useEffect(() => {
    if (projectId) {
      fetchStatusMaster();
    }
  }, [projectId, fetchStatusMaster]);

  // 행 추가 핸들러 (다이얼로그 열기)
  const handleAddRow = () => {
    setCurrentData(null);
    setDataDialogMode('add');
    setDataDialogOpen(true);
  };

  // 행 삭제 핸들러
  const handleDeleteRow = async () => {
    if (selectedRows.length === 0) {
      alert("삭제할 항목을 선택하세요.");
      return;
    }

    if (!window.confirm(`선택한 ${selectedRows.length}개 항목을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const statusIds = selectedRows.map(row => row.statusId);
      
      await axiosPrivate.post('/table/statusmaster/delete', {
        project_id: projectId,
        group_code,
        project_code,
        statusIds
      });

      // 데이터 다시 로드
      await fetchStatusMaster();
      
      alert("삭제되었습니다.");
    } catch (err) {
      console.error("삭제 실패:", err);
      alert("삭제 중 오류가 발생했습니다.");
    }
  };

  // 행 저장 핸들러 (필요 없음, 팝업에서 즉시 저장)
  const handleSaveRow = async () => {
    alert("개별 항목을 더블클릭하여 수정하거나 추가 버튼을 사용하세요.");
  };

  // 셀 값 변경 핸들러 (사용 안함)
  const onCellValueChanged = (params) => {
    // 팝업 방식이므로 사용 안함
  };

  // 선택 변경 핸들러
  const onSelectionChanged = (event) => {
    const selectedNodes = event.api.getSelectedNodes();
    const selectedData = selectedNodes.map(node => node.data);
    setSelectedRows(selectedData);
  };
  
  // 행 더블클릭 핸들러 (편집 다이얼로그 열기)
  const handleRowDoubleClicked = (event) => {
    setCurrentData(event.data);
    setDataDialogMode('edit');
    setDataDialogOpen(true);
  };

  // 다이얼로그 저장 핸들러
  const handleSaveData = async (data, mode) => {
    try {
      let endpoint = '';
      
      if (mode === 'add') {
        endpoint = '/table/statusmaster/add';
      } else if (mode === 'edit') {
        endpoint = '/table/statusmaster/update';
      }
      
      await axiosPrivate.post(endpoint, {
        project_id: projectId,
        group_code,
        project_code,
        data
      });
      
      // 데이터 다시 로드
      await fetchStatusMaster();
      
      // 다이얼로그 닫기
      setDataDialogOpen(false);
      
      // 성공 메시지
      alert(mode === 'add' ? '추가되었습니다.' : '수정되었습니다.');
      
      // 그리드 새로고침
      setTimeout(() => {
        if (gridRef.current && gridRef.current.api) {
          gridRef.current.api.sizeColumnsToFit();
          gridRef.current.api.autoSizeAllColumns();
        }
      }, 100);
    } catch (err) {
      console.error("데이터 저장 실패:", err);
      alert(`데이터 저장 중 오류가 발생했습니다: ${err.response?.data?.message || err.message}`);
    }
  };

  // 다이얼로그 닫기
  const handleCloseDialog = () => {
    setDataDialogOpen(false);
  };

  // 엑셀 내보내기
  const handleExportExcel = () => {
    const ws = XLSX.utils.json_to_sheet(rowData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "StatusMaster");
    XLSX.writeFile(wb, `StatusMaster_${project_code}.xlsx`);
  };

  return (
    <Grid container spacing={3} sx={{ height: "100%" }}>
      <Grid size={{ xs: 12 }} sx={{ height: "100%"}}>
        <Card sx={{
          height: "100%",
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '0',
          bgcolor: theme.palette.mode === 'dark' 
            ? 'var(--mui-palette-background-default)' 
            : 'var(--mui-palette-background-paper)'
        }}>
          <CardHeader
            title={`상태 유형 테이블`}
            subheader={`프로젝트: ${projectName}`}
            action={
              <Box display="flex" gap={1}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportExcel}
                >
                  Excel 다운로드
                </Button>
              </Box>
            }
          />
          
          <DatagridButtonGroup
            handleAddRow={handleAddRow}
            handleDeleteRow={handleDeleteRow}
            handleSaveRow={handleSaveRow} // 버튼은 유지하되 메시지 표시
            selectionModel={selectedRows.length > 0}
            hideSaveButton={true} // 저장 버튼 숨김 (개별 저장이므로)
          />

          <CardContent sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            paddingInline: 6,
            paddingBlock: 2,
            height: 0
          }}>
            <CommonAgGrid
              gridRef={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              onSelectionChanged={onSelectionChanged}
              onCellValueChanged={onCellValueChanged}
              onRowDoubleClicked={handleRowDoubleClicked}
              pageSize={pageSize}
            />
          </CardContent>
        </Card>
      </Grid>
      
      {/* 데이터 다이얼로그 */}
      <StatusMasterDialog
        open={dataDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveData}
        data={currentData}
        mode={dataDialogMode}
        projectId={projectId}
        group_code={group_code}
        project_code={project_code}
        existingData={rowData}
      />
    </Grid>
  );
};

export default StatusMaster;
